import formatCurrency from 'core-lib/utils/currency';
import { getDefaultAttributes, trackEvent } from 'core-lib/utils/events';
import {
  FeedbackGroupState,
  profilePicColors,
  TABS,
  WidgetType,
} from '../constants';
import { ClientType } from '../types';
import ClientDetails from '../types/ClientDetails';
import { CardDataType } from '../types/Listing';
import { semanticColors } from 'ui-v2/tokens/colors';

const getClientName = (name: string, threshold = 13) => {
  let newName = name;
  if (name.length > threshold) {
    newName = name;
  }
  return newName;
};

export const formatClientCount = (
  clientList: ClientDetails[] = [],
  clientsToFormat = 5
) => {
  let formattedString = '';
  for (let i = 0; i < clientsToFormat && i < clientList.length; i++) {
    if (i > 0) {
      formattedString += ', ';
    }
    formattedString += `${getClientName(clientList[i].clientName)}`;
  }
  return formattedString;
};

export enum CallDirectoryEvents {
  PageLoaded = 'page_loaded',
  CtaClicked = 'cta_clicked',
  CardClicked = 'card_clicked',
  LeadSubmitted = 'lead_submitted',
  SectionViewed = 'section_viewed',
  TabClicked = 'tab_clicked',
  PopupViewed = 'popup_viewed',
}

export enum CallDirectoryAttributes {
  Category = 'category',
  SubCategory = 'subcategory',
  PageName = 'page_name',
  Section = 'section_name',
  OptionSelected = 'option_selected',
  Model = 'model',
  CtaName = 'cta_name',
  Dealer = 'dealer',
  CardName = 'card_name',
  TabName = 'tab_name',
  PopupName = 'popup_name',
}

export const trackCallDirectoryEvents = (eventName, attributes) => {
  trackEvent({
    name: eventName,
    defaultAttr: getDefaultAttributes(),
    attributes: {
      [CallDirectoryAttributes.Category]: 'Test drive',
      [CallDirectoryAttributes.SubCategory]: 'Directory',
      ...attributes,
    },
  });
};

export enum CallDirectoryPages {
  Home = 'directory_home',
  Listing = 'listing_page',
  Permission = 'directory_permission',
  Syncing = 'directory_syncing',
  Refer = 'referral_listing',
  Description = 'car_detail',
  Search = 'search_page',
  Rating = 'phonebook_rating_page',
  ClientCars = 'client_cars_page',
  FriendsLeaderboard = 'friends_and_leaderboard',
  UserActivity = 'user_activities',
}

export enum CallDirectoryPopups {
  ExitPopup = 'exit_popup',
  HowToGetFuelPopup = 'how_to_get_fuel_popup',
  SampleReviewPopup = 'sample_review_popup',
  AppRatingPopUp = 'app_rating_popup',
}

export const getShortName = (name) => {
  if (!name) return null;
  const [firstLetter, lastLetter] = name?.split(' ');
  let text = '';
  const re = /[a-zA-Z]/;

  const firstIdx = firstLetter?.search(re);
  const secondIdx = lastLetter?.search(re);

  if (firstIdx > -1) {
    text += firstLetter?.[firstIdx];
  }
  if (secondIdx > -1) {
    text += lastLetter?.[secondIdx];
  }

  return text?.toLocaleUpperCase();
};

export const getBgColor = () => {
  return profilePicColors[Math.floor(Math.random() * profilePicColors?.length)];
};

export const formatClientData = ({ clientList, dealerClientList }) => {
  const result = [];
  clientList?.map((item) => {
    const dealerClient = dealerClientList?.find(
      (e) => item?.clientId === e?.clientId
    );

    if (dealerClient) {
      const obj = {
        ...item,
        vehicleDetails: {
          ...item?.vehicleDetails,
          ...dealerClient?.vehicleDetails,
        },
      };
      result.push(obj);
    }
  });
  return result;
};

export const LIMIT = 15;

export const getSearchPayload = ({
  searchText = '',
  widgetType,
  page = 1,
  filters = {},
  isPrivate,
}: {
  searchText?: string;
  widgetType: WidgetType;
  page?: number;
  filters?: any;
  isPrivate?: boolean;
}) => {
  const obj: any = {};
  const validFilters: any = {};

  // Validate and populate validFilters
  Object.keys(filters || {})?.forEach((key) => {
    if (filters?.[key]?.length > 0) {
      validFilters[key] = filters[key];
    } else if (!!filters?.[key]) {
      validFilters[key] = filters[key];
    }
  });

  // Add payloads for each tab or widget type
  if (widgetType === WidgetType.All) {
    const tabs = [
      ...TABS,
      //  { label: 'EV', value: WidgetType.EvVehicles },
      { label: 'Hybrid', value: WidgetType.HybridVehicles },
    ];
    tabs?.forEach((item) => {
      const widgetPayload: any = {
        page: 1,
        ...validFilters,
      };
      widgetPayload.limit = item?.value === WidgetType.ParkplusMembers ? 10 : 3;

      // Add to obj based on isPrivate condition
      if (item?.value === WidgetType.ParkplusMembers) {
        if (!isPrivate) {
          obj[item.value] = widgetPayload;
        }
      } else {
        obj[item.value] = widgetPayload;
      }
    });
  } else {
    obj[widgetType] = {
      page,
      limit: LIMIT,
      ...validFilters,
    };
  }
  if (searchText) {
    obj.search_term = searchText;
  }
  return obj;
};

export const getParsedData = ({
  isPrivateMode,
  widgetType,
  cardData,
}: {
  isPrivateMode: boolean;
  widgetType: WidgetType;
  cardData: CardDataType;
}) => {
  const {
    dealerName,
    dealerImage,
    dealerRating,
    dealerAddress,
    brandName,
    modelName,
    modelImage,
    modelRating,
    userClientsCount = 0,
    userClients = [],
    modelPrice,
    dealerList = [],
    reviewsCount = 0,
  } = cardData;

  const isDealerOrService = [
    WidgetType.Dealers,
    WidgetType.ServicingDone,
  ].includes(widgetType);
  const isDealer = widgetType === WidgetType.Dealers;
  const isTestDriveBooked = widgetType === WidgetType.TestDriveBooked;
  const isSellCar = widgetType === WidgetType.SellCar;
  const isVehicle = [WidgetType.UniqueCarOwned, WidgetType.PremiumCar].includes(
    widgetType
  );

  const clientText = (count: number) =>
    `${count} ${count === 1 ? 'friend' : 'friends'}`;

  const otherClientsText = () => {
    let text = userClients?.[0]?.fullName || '';
    const count = userClientsCount - 1;
    if (count > 1) {
      text += ` & ${count} others`;
    } else if (count === 1) {
      text += ` & ${count} other`;
    }
    return text;
  };

  const obj: any = {
    title: isDealerOrService ? dealerName : `${brandName} ${modelName}`,
    image: isDealerOrService ? dealerImage : modelImage,
    rating: isDealerOrService ? dealerRating : modelRating,
    subtitle: isDealerOrService
      ? dealerAddress
      : isPrivateMode
      ? clientText(userClientsCount)
      : otherClientsText(),
    ...(isTestDriveBooked && {
      isSpanClickable: true,
      spanSubtitle: '',
      spanTitle: 'Booked from',
    }),
    ...(isSellCar && {
      subtitle: `${brandName} ${modelName} • ${formatCurrency(
        modelPrice,
        true
      )}`,
    }),
    showIcon: true,
  };

  if (isDealerOrService) {
    obj.spanTitle = `${clientText(userClientsCount)}`;
    if (isDealer) {
      obj.spanTitle += ' bought from here';
    } else {
      obj.spanTitle += ' got their cars serviced';
    }
  }

  if (isSellCar) {
    obj.title = isPrivateMode
      ? clientText(userClientsCount)
      : otherClientsText();
    obj.showIcon = false;
  }

  //product requirement to show only count in test drive
  if (isTestDriveBooked) {
    obj.subtitle = `${userClientsCount} ${
      userClientsCount === 1 ? 'friend' : 'friends'
    }`;
    obj.showDealerList = true;
    obj.spanTitle = `Booked from ${
      dealerList?.length === 1
        ? 'this dealer'
        : `these ${dealerList?.length} dealers`
    }`;
  }

  if (isVehicle && reviewsCount > 0) {
    obj.spanTitle = `${reviewsCount} friends ${
      reviewsCount === 1 ? 'review' : `reviews`
    }  available`;
  }

  if (isDealerOrService && reviewsCount > 0) {
    obj.dealerReviewText = `${reviewsCount} friends ${
      reviewsCount === 1 ? 'review' : `reviews`
    }  available`;
  }

  return obj;
};

export const getCarFeedbackState = ({
  data,
  userId,
}: {
  data: ClientType;
  userId: any;
}) => {
  let state = FeedbackGroupState.addCarRating;
  if (!!data?.modelRating) {
    state = FeedbackGroupState.rated;
  } else if (data?.addCarRatingNotificationUsers?.includes(userId)) {
    state = FeedbackGroupState.feedbackRequested;
  }

  return state;
};

export const getDealerFeedbackState = ({
  data,
  userId,
}: {
  data: ClientType;
  userId: any;
}) => {
  let state = FeedbackGroupState.addDealerRating;
  if (
    data?.addDealerNotificationUsers?.includes(Number(userId)) ||
    data?.addRatingNotificationUsers?.includes(Number(userId))
  ) {
    state = FeedbackGroupState.feedbackRequested;
  } else if (!!data?.dealerName && !!data?.dealerRating) {
    state = FeedbackGroupState.rated;
  } else if (!data?.dealerId) {
    state = FeedbackGroupState.addDealer;
  }

  return state;
};

export const formatNumber = (num: number) => {
  const formattedNumber = new Intl.NumberFormat('en-US').format(num);
  return formattedNumber;
};

export const faq = [
  {
    question: 'What is Park+ community?',
    answer:
      'These community members collaborate to enhance the car ownership experience for everyone. We believe that the power of this community would help in making a more personalized, efficient and convenient way of not only buying a car but gradually would also help in day-to-day car ownership issues. The top-rated community members would get access to exclusive car offers, servicing deals and the latest updates to keep up with industry trends.',
  },
  {
    question: 'Can I opt out of showing my car information?',
    answer:
      'Yes, you can opt out of sharing your car information. Private mode ensures that your contact data, which was synced with your permission, is no longer updated or shared. In Private Mode, your information remains invisible to your friends. You won’t be able to see their car experiences either.',
  },
  {
    question: 'What is Private Mode?',
    answer:
      'Private Mode is a feature that allows you to hide your car-related information from your friends and contacts from the current date onwards. However, your reviews about your car and dealer will be visible to your friends in anonymous friend category ',
  },
  {
    question: 'Who can see my car information?',
    answer:
      'Your car is a valuable asset, and many of your friends, family, and colleagues may already know about it. This information is visible to your society members, office colleagues and friends who have your mobile number in their contact book. The information you share on Park+ helps them learn about your ownership experience and make better car-buying decisions.',
  },
  {
    question: 'How can I improve my information on Park+?',
    answer:
      'Park+ is building a trusted community of car owners. You can enhance your profile by syncing your contacts, which allows you to connect with other car owners in your society and workplace. By contributing actively, you can become a senior member of the community whom others can reach out to for advice.',
  },
  {
    question: 'What are the benefits of sharing my contact?',
    answer:
      'Being part of the Park+ community comes with several advantages. By sharing your feedback over time, you gain access to exclusive benefits such as test drives of new vehicles, invitations to launch events, OEM drive initiatives, and opportunities to meet brand celebrities. Additionally, you can enjoy special servicing offers and other exciting perks designed for engaged community members. Your insights help build a trusted network of car owners, making the car ownership experience better for everyone.',
  },
  {
    question: 'How can I delete my contact information?',
    answer:
      'Deleting your previously synced contact information is simple. You can submit a request through Park+ Support, and our team will assist you in the process.',
  },
];

export const MIN_SHOW_RESTRICTED = 10;

export const BLUR_CARD_IMAGE =
  'https://parkplus-bkt-img.parkplus.io/production/phonebook/public/blur_20250219192046972069_80230bde-1b6d-4ac2-8c95-6a90b5d9d63f.png';

export const BLUR_CLIENT_IMAGE =
  'https://parkplus-bkt-img.parkplus.io/production/phonebook/public/client_blur_20250302114640723071_a82f9b4a-c3e3-4abe-a112-a0d5b67613b4.png';

export const BLUR_TEST_DRIVE_LI =
  'https://parkplus-bkt-img.parkplus.io/production/pb/public/blur_20250306184243048905_5fdc0f30-071f-4569-b868-667b9d49c1b6.png';

export const DUMMY_PB =
  'https://parkplus-bkt-img.parkplus.io/production/valet/public/supervisor_20241107155607967532.png';

export const CROWN_IMG =
  'https://parkplus-bkt-img.parkplus.io/production/phonebook/public/blur_image_20250407102500175341_7b72bdcf-b7dd-47be-922d-44a210d3f83d.png';

export const timeRangeOptions = [
  {
    label: 'Last 3 Months',
    value: '90',
  },
  {
    label: 'Last Month',
    value: '30',
  },
];

export const INIT_TIME_RANGE = Number(timeRangeOptions[0].value);

export const getRankBg = (rank) => {
  switch (rank) {
    case 1:
      return semanticColors.bg.brand_tertiary;
    case 2:
      return semanticColors.bg.positive_secondary;
    case 3:
      return semanticColors.bg.warning_secondary;
  }
};

export const getRankBorder = (rank) => {
  switch (rank) {
    case 1:
      return semanticColors.bg.brand;
    case 2:
      return semanticColors.bg.positive;
    case 3:
      return semanticColors.bg.warning;
  }
};

export const getRankIconColor = (rank) => {
  switch (rank) {
    case 1:
      return 'brand';
    case 2:
      return 'positive';
    case 3:
      return 'warning';
  }
};

export const topRanks = [1, 2, 3];
